/**
 * The one typed way to read Supabase rows.
 *
 * Every service and hook that touches PostgREST describes its query result
 * through these aliases instead of hand-writing a shape and casting the
 * response into it. The generated `Database` type is the single source of
 * truth: column types, nullability and enum unions always come from
 * `lib/types/database.ts`, so a migration that renames or retypes a column
 * breaks the build at the mapper instead of silently mis-typing a row at
 * runtime.
 *
 * Convention:
 *   - `Row<'vehicles'>`                 — the full table row
 *   - `Cols<'vehicles', 'id' | 'title'>` — exactly the columns a select asks for
 *   - `WithEmbed<Cols<'rentals', …>, { vehicles: EmbedOne<'vehicles', 'title'> }>`
 *                                        — a selected row plus its PostgREST embeds
 *   - `RpcRow<'search_vehicles'>`        — one element of an RPC result set
 *
 * Embeds are modelled explicitly because the generated types describe tables,
 * not joins: `EmbedOne` for a to-one relation (PostgREST returns `null` when
 * the FK is null or RLS hides the parent) and `EmbedMany` for a to-many one.
 */
import { Constants } from './database'
import type { Database, Json } from './database'

/** The generated JSON type backing every `jsonb` column. */
export type { Json } from './database'

type PublicSchema = Database['public']

// ── Tables ──────────────────────────────────────────────────────────────────

/** Every table name in the public schema. */
type TableName = keyof PublicSchema['Tables']

/** A full row of table `T`, as returned by `.select('*')`. */
export type Row<T extends TableName> = PublicSchema['Tables'][T]['Row']

/** The insert payload of table `T`, as accepted by `.insert()`. */
export type InsertRow<T extends TableName> = PublicSchema['Tables'][T]['Insert']

/** The update payload of table `T`, as accepted by `.update()`. */
export type UpdateRow<T extends TableName> = PublicSchema['Tables'][T]['Update']

/**
 * The subset of table `T` that a `.select('a,b,c')` returns.
 * Keeps the mapper honest: asking for a column the select omits is a type error.
 */
export type Cols<T extends TableName, K extends keyof Row<T>> = Pick<Row<T>, K>

// ── Embeds ──────────────────────────────────────────────────────────────────

/**
 * A selected row plus the relations its select string embeds.
 * `TEmbeds` keys are the embed aliases exactly as they appear in the select
 * (`vehicles`, `host:profiles!fkey` → `host`, …).
 */
export type WithEmbed<TRow, TEmbeds extends object> = TRow & TEmbeds

/**
 * A to-one embedded relation. Nullable because PostgREST returns `null` when
 * the foreign key is null or RLS hides the referenced row.
 */
export type EmbedOne<T extends TableName, K extends keyof Row<T>> = Cols<T, K> | null

/** A to-many embedded relation. */
export type EmbedMany<T extends TableName, K extends keyof Row<T>> = Cols<T, K>[]

// ── Enums ───────────────────────────────────────────────────────────────────

/** Every enum name in the public schema. */
export type EnumName = keyof PublicSchema['Enums']

/** A Postgres enum union, e.g. `DbEnum<'rental_status'>`. */
export type DbEnum<E extends EnumName> = PublicSchema['Enums'][E]

/**
 * The generated runtime value lists for every enum, widened to `string[]` so
 * they can be membership-tested against untrusted input.
 */
const DB_ENUM_MEMBERS: { readonly [E in EnumName]: readonly string[] } = Constants.public.Enums

/**
 * Runtime guard for values that arrive outside the typed query path — RPCs
 * declared as returning `Json`, deep links, cached payloads. Use it instead of
 * asserting an enum with a cast.
 */
export function isDbEnum<E extends EnumName>(name: E, value: unknown): value is DbEnum<E> {
  return typeof value === 'string' && DB_ENUM_MEMBERS[name].includes(value)
}

// ── Functions (RPC) ─────────────────────────────────────────────────────────

/** Every RPC name in the public schema. */
type FunctionName = keyof PublicSchema['Functions']

/** The full return value of RPC `F`. */
type RpcResult<F extends FunctionName> = PublicSchema['Functions'][F]['Returns']

/** One element of RPC `F`'s result — the scalar itself when it is not a set. */
export type RpcRow<F extends FunctionName> =
  RpcResult<F> extends readonly (infer Element)[] ? Element : RpcResult<F>

// ── jsonb readers ───────────────────────────────────────────────────────────
//
// `jsonb` columns and RPCs declared `Returns: Json` have no generated shape,
// so their contents are whatever some past app version or backfill wrote.
// These readers narrow `Json` by inspection instead of asserting a shape the
// schema never promised.

/** Narrows a `Json` value to its object branch; anything else reads as empty. */
export function jsonObject(value: Json | undefined): { [key: string]: Json | undefined } {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}
  return value
}

/** Reads one property off a `Json` object. */
export function jsonField(value: Json | undefined, key: string): Json | undefined {
  return jsonObject(value)[key]
}

/** Reads a `Json` value as a string, or `null` when it is anything else. */
export function jsonString(value: Json | undefined): string | null {
  return typeof value === 'string' ? value : null
}
